import {
  BLOCKING_RESERVATION_STATUSES,
  type Reservation,
  type ReservationStatus,
} from '@/modules/reservations/types';
import { isDateRangeOverlapping } from '@/modules/reservations/utils/dateRange';

export function isBlockingReservation(reservation: Reservation): boolean {
  return (BLOCKING_RESERVATION_STATUSES as readonly string[]).includes(
    reservation.status,
  );
}

export function reservationUsesAnyItem(
  reservation: Reservation,
  itemIds: string[],
): boolean {
  const set = new Set(itemIds);
  return reservation.inventoryItemIds.some((id) => set.has(id));
}

export function findOverlappingBlockingReservations(
  reservations: Reservation[],
  itemIds: string[],
  startDate: string,
  endDate: string,
  excludeReservationId?: string,
): Reservation[] {
  return reservations.filter((r) => {
    if (excludeReservationId && r.id === excludeReservationId) return false;
    if (!isBlockingReservation(r)) return false;
    if (!reservationUsesAnyItem(r, itemIds)) return false;
    return isDateRangeOverlapping(
      startDate,
      endDate,
      r.startDate,
      r.endDate,
    );
  });
}

export type ReservationListFilters = {
  status?: ReservationStatus | 'all';
  search?: string;
  dateFrom?: string;
  dateTo?: string;
};

export function filterReservations(
  reservations: Reservation[],
  filters: ReservationListFilters,
): Reservation[] {
  let result = [...reservations];

  if (filters.status && filters.status !== 'all') {
    result = result.filter((r) => r.status === filters.status);
  }

  if (filters.dateFrom) {
    result = result.filter((r) => r.endDate >= filters.dateFrom!);
  }

  if (filters.dateTo) {
    result = result.filter((r) => r.startDate <= filters.dateTo!);
  }

  const q = filters.search?.trim().toLowerCase();
  if (q) {
    result = result.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.clientName.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q),
    );
  }

  return result.sort((a, b) => b.startDate.localeCompare(a.startDate));
}
